/**
 *@file   Constants
 */
import type { Country } from "@/constants/countries";
import { DEUTSCHLAND, OESTERREICH, SCHWEIZ } from "@/constants/countries";
export const TOTAL_DAYS_IN_YEAR = 365;

export enum ServiceIDs {
  FlatMobileTwoMonth = 131601,
  FlatMobileTwoYear = 131611,

  FlatMobileThreeMonth = 131603,
  FlatMobileThreeYear = 131613,

  FlatMobileOneMonth = 131602,
  FlatMobileOneYear = 131612,

  Flat04Month = 131101,
  Flat04Year = 131111,

  Flat1Month = 131102,
  Flat1Year = 131112,

  Flat10Month = 131103,
  Flat10Year = 131113,

  Flat50Month = 131104,
  Flat50Year = 131114,

  Flat300Month = 131105,
  Flat300Year = 131115,

  Flat2000Month = 131121,
  Flat2000Year = 131122,

  FlatHome4G = 131801,
  FlatHome5G = 131811,
}

export enum ChargeTypeIDs {
  VoiceOption = 400,
}

export const starterKit = "Starterpaket";

/*
          Regular Expressions
 */

/**
 * Text field validation - excludes delimiter characters
 *
 * @valid "Any text here", "München", "Street Name 123"
 * @invalid "" (empty), "text,with,commas" (comma), "text;with;semicolons" (semicolon)
 */
export const DEFAULT_TEXT_FIELD_REGEX: RegExp = /^[^,;]+$/;

/** Max length for SIM / device alias (checkout + portal). */
export const SIM_ALIAS_MAX_LENGTH = 50;

/**
 * Strong password validation regex with comprehensive character requirements
 *
 * @description Validates passwords with strict security requirements
 * @pattern Complex lookaheads + character class validation with length constraints
 *
 * Requirements:
 * - At least one lowercase letter (a-z)
 * - At least one uppercase letter (A-Z)
 * - At least one digit (0-9)
 * - At least one special character from: =+-^$*.[]{}()?"!@#%&/\,><':;|_~`
 * - Length between 8-30 characters
 * - Only allows letters, digits, and specified special characters
 *
 * @valid "Password123!", "MySecure@Pass1", "Complex$Pass9", "StrongPwd#42"
 * @valid "Test123$", "Admin@2024", "User!Pass8", "Secure*Key1"
 *
 * @invalid "password" (no uppercase/digit/special), "PASSWORD123" (no lowercase/special)
 * @invalid "Pass123" (no special char), "Pass@" (too short), "Pass!" (no digit/uppercase)
 * @invalid "VeryLongPasswordThatExceeds30Characters!" (too long), "Pässwörd123!" (non-ASCII)
 *
 * @security Strong password policy enforcing complexity and preventing common weak passwords
 */
export const DEFAULT_PASSWORD_REGEX: RegExp =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~`])[a-zA-Z\d=+\-^$*.[\]{}()?"!@#%&/\\,><':;|_~`]{8,30}$/;

/**
 * Email Regex used by the backend
 *
 * @description RFC 5322 compliant email validation with support for quoted strings and IP addresses
 *
 * Features:
 * - Local part: Supports standard characters, dots, and quoted strings
 * - Special characters: !#$%&'*+/=?^_`{|}~- allowed in local part
 * - Quoted strings: Allows quoted local parts with spaces and special chars
 * - Domain validation: Standard domain format with proper TLD
 * - IP addresses: Supports IPv4 addresses in square brackets [192.168.1.1]
 * - RFC 5322 compliance: Follows official email specification
 *
 * @valid "user@example.com", "test.email+tag@domain.co.uk", "user+tag@example.org"
 * @valid "firstname.lastname@company.com", "user_name@domain-name.com"
 * @valid "special!chars@domain.com", "quoted@string@domain.com" (quoted)
 * @valid "user@[192.168.1.1]" (IP address), "user@sub.domain.example.com"
 *
 * @invalid "invalid" (no @), "user@" (no domain), "@domain.com" (no local)
 * @invalid "user@domain" (no TLD), "user@@domain.com" (double @)
 * @invalid "user name@domain.com" (unquoted space), ".user@domain.com" (leading dot)
 */
export const BACKEND_EMAIL_REGEX: RegExp =
  /^(?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"*")@(?:(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[A-Za-z0-9-]*[A-Za-z0-9])])$/;

/**
 * Name validation regex for international names
 *
 * @description Validates personal names using Unicode letter categories
 *
 * Features:
 * - Requires at least one Unicode letter or mark (prevents punctuation-only names)
 * - Supports all Unicode letters (\p{L}) and marks (\p{M}) for international compatibility
 * - Allows Unicode decimal numbers (\p{Nd}) for names with numbers
 * - Permits common name punctuation: spaces, hyphens, apostrophes, periods, commas
 * - Unicode flag (u) enables proper Unicode property matching
 *
 * Unicode categories:
 * - \p{L}: Letters (Latin, Cyrillic, Chinese, Arabic, etc.)
 * - \p{M}: Marks (accents, diacritics, combining characters)
 * - \p{Nd}: Decimal numbers (0-9, ٠-٩, etc.)
 *
 * @valid "John Smith", "María José", "李小明", "O'Connor", "King Henry VIII"
 * @invalid "123" (numbers only), "---" (punctuation only), "" (empty), "Name@Email" (@ symbol)
 */
export const INTERNATIONAL_NAME_REGEX: RegExp =
  /^(?=.*[\p{L}\p{M}])[\p{L}\p{M}\p{Nd}\s\-'.,]+$/u;

/**
 * Street name  regex for DACH region (Germany, Austria, Switzerland)
 *
 * @description Validates street names for DACH region
 *
 * Features:
 * - Requires at least one alphanumeric character
 * - Supports accented characters (À-ÿ) for European languages
 * - Allows common punctuation: spaces, hyphens, apostrophes, periods, commas, parentheses
 *
 * @valid "Hauptstraße", "Karl-Marx-Platz", "Rue de la Paix", "St. Gallen-Straße"
 * @invalid "---" (punctuation only), "Street@" (@ symbol), "" (empty)
 */
export const DACH_STREET_NAME_REGEX: RegExp =
  /^(?=.*[a-zA-ZÀ-ÿ0-9])[a-zA-ZÀ-ÿ0-9\s\-'.,()]+$/;

/**
 * Street-Number regex for DACH region (Germany, Austria, Switzerland)
 *
 * @description Validates house numbers for DACH region
 *
 * Features:
 * - Requires at least one digit
 * - Supports alphanumeric characters for apartment/unit designations
 * - Allows hyphens and slashes for ranges and subdivisions
 *
 * @valid "12", "15a", "23B", "12-14", "34/2", "5-7a"
 * @invalid "abc" (no digits), "12 a" (space), "12.5" (period), "" (empty)
 */
export const DACH_HOUSE_NUMBER_REGEX: RegExp = /^(?=.*\d)[0-9a-zA-Z\-/]+$/;

/**
 * Postal-Code regex for non-DACH countries
 * @description Accepts alphanumeric characters and spaces, up to 10 characters
 */
const GENERIC_POSTAL_CODE_REGEX: RegExp =
  /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9 ]{1,10}$/;

const DE_POSTAL_CODE_REGEX: RegExp = /^[0-9]{5}$/;
const AT_CH_POSTAL_CODE_REGEX: RegExp = /^[0-9]{4}$/;

export function getPostalCodePattern(country?: Country): RegExp {
  if (country?.ALPHA2 === DEUTSCHLAND.ALPHA2) {
    return DE_POSTAL_CODE_REGEX;
  }
  if (
    country?.ALPHA2 === OESTERREICH.ALPHA2 ||
    country?.ALPHA2 === SCHWEIZ.ALPHA2
  ) {
    return AT_CH_POSTAL_CODE_REGEX;
  }

  return GENERIC_POSTAL_CODE_REGEX;
}

/**
 * ICCID (Integrated Circuit Card Identifier) validation regex
 *
 * @description Validates SIM card ICCID numbers with flexible formatting and length support
 *
 * Features:
 * - Supports multiple ICCID formats with varying lengths (typically 18-22 digits)
 * - Allows optional spacing between digit groups for readability
 * - Validates the standard ICCID structure: IIN (6 digits) + Account ID + Check digit
 * - Handles both full-length and partial ICCID formats
 *
 * Pattern breakdown:
 * - First 6 digits: Issuer Identification Number (IIN)
 * - Next groups: Account identification and individual account identification
 * - Final digits: Check digit(s) and padding
 * - Optional spaces between groups for formatting
 *
 * Supported formats:
 * - Full format: 6-4-4-4-2 digits (e.g., "123456 7890 1234 5678 90")
 * - Alternative: 6-4-4-4-X digits (variable ending)
 * - Shorter: 6-4-4-X digits (variable ending)
 * - Minimal: 6-4-X digits (variable ending)
 * - Basic: 6-X digits (variable ending)
 *
 * @valid "123456789012345678" (18 digits), "12345678901234567890" (20 digits)
 * @valid "123456 7890 1234 5678 90" (with spaces), "123456789012345678901" (21 digits)
 * @valid "1234567890123456" (16 digits), "123456789012345" (15 digits)
 *
 * @invalid "12345" (too short), "abcd567890123456789" (letters), "123-456-789" (hyphens)
 * @invalid "" (empty), "123 456 789" (wrong grouping)
 *
 * @see https://en.wikipedia.org/wiki/SIM_card#ICCID
 */
export const ICCID_REGEX: RegExp =
  /^(\d{6})\s?(\d{4})\s?(\d{4})\s?(\d{4})\s?(\d\d)$|^(\d{6})\s?(\d{4})\s?(\d{4})\s?(\d{4})(\d+)$|^(\d{6})\s?(\d{4})\s?(\d{4})(\d+)$|^(\d{6})\s?(\d{4})(\d+)$|^(\d{6})(\d*)?$/;

/**
 * Swiss phone number validation regex
 *
 * @description Validates Swiss phone numbers in the international format with area code
 *
 * Features:
 * - Enforces Swiss country code: +41 (0) format
 * - Supports variable-length area codes (1-2 digits)
 * - Accommodates different local number formats
 * - Allows optional spacing between number groups
 * - Validates Swiss numbering plan structure
 *
 * Format structure:
 * - Country code: +41
 * - National prefix: (0) - shown but not dialed internationally
 * - Area code: 1-2 digits (e.g., 44 for Zurich, 21 for Lausanne)
 * - Local number: Divided into groups, total length varies by area
 *
 * Swiss numbering plan:
 * - Geographic numbers: +41 (0) XX XXX XX XX
 * - Mobile numbers: +41 (0) 7X XXX XX XX
 * - Special services: Various formats
 *
 * @valid "+41 (0) 44 123 45 67" (Zurich), "+41 (0) 21 123 45 67" (Lausanne)
 * @valid "+41 (0) 79 123 45 67" (mobile), "+41 (0) 800 123 456" (toll-free)
 * @valid "+41 (0) 1 234 56 78" (single digit area code)
 *
 * @invalid "+41 44 123 45 67" (missing (0)), "+49 (0) 44 123 45 67" (wrong country)
 * @invalid "044 123 45 67" (missing country code), "+41 (0) 444 123 45 67" (area code too long)
 * @invalid "+41 (0)" (incomplete), "+41 (0) abc 123 45 67" (letters in area code)
 *
 * @see https://en.wikipedia.org/wiki/Telephone_numbers_in_Switzerland
 */
export const PHONE_REGEX: RegExp =
  /^(\+41\s\(0\))\s?(\d{1,2})\s?(\d{0,3})\s?(\d{0,2})\s?(\d{0,2})$/;
